/**
 * CTC → Reports: the two legacy Excel reports (Commodity Contract Summary / Commodity Contracts
 * Details). Column definitions live here so the Reports page (column checkboxes) and the server that
 * builds the .xlsx (functions-ctc/src/reports.ts) can't disagree. One row per order matching the
 * filters (by default: completed orders requested in the period).
 */

import { CtcPoState, CtcPoStatusCode, ctcPoStateOf } from "./ctcLifecycle";

export type CtcReportKind = "summary" | "details";
export type CtcReportCellKind = "text" | "date" | "money" | "number";

export interface CtcReportColumn {
    key: string;
    label: string;
    /** Header grouped above the column (e.g. "Base Currency - KWD"). */
    group?: string;
    kind: CtcReportCellKind;
    width: number;
}

export interface CtcReportDefinition {
    title: string;
    columns: CtcReportColumn[];
}

/** Report columns with the broker commission: left out for bank makers / checkers (ctcHidesBrokerCommission). */
export const CTC_REPORT_BROKER_COMMISSION_COLUMNS: ReadonlySet<string> = new Set(["brokerCommKwd"]);

const TRX = "Trx. Cry";
const CUSTOMER_CCY = "Customer's Preferred Currency";
const BROKER_CCY = "Broker's Preferred Currency";
const BASE = "Base Currency - KWD";

export const CTC_REPORTS: Record<CtcReportKind, CtcReportDefinition> = {
    summary: {
        title: "Commodity Contract Summary",
        columns: [
            { key: "reference", label: "Trx. Ref.", kind: "text", width: 18 },
            { key: "date", label: "Date", kind: "date", width: 12 },
            { key: "customerGroup", label: "Customer Group", kind: "text", width: 18 },
            { key: "customer", label: "Customer", kind: "text", width: 26 },
            { key: "broker", label: "Broker", kind: "text", width: 18 },
            { key: "clientRef", label: "Client's Ref. No.", kind: "text", width: 16 },
            { key: "beneficiaryAccount", label: "Beneficiary's Account No.", kind: "text", width: 20 },
            { key: "tradingType", label: "Trading Type", kind: "text", width: 22 },
            { key: "status", label: "Status", kind: "text", width: 12 },
            { key: "trxCurrency", label: "Trx. Cry", kind: "text", width: 9 },
            { key: "valueTrx", label: "Value of Trx.", group: TRX, kind: "money", width: 16 },
            { key: "valueKwd", label: "Value of Trx.", group: BASE, kind: "money", width: 16 },
            { key: "brokerCommKwd", label: "Broker Comm.", group: BASE, kind: "money", width: 14 },
        ],
    },
    details: {
        title: "Commodity Contracts Details",
        columns: [
            { key: "reference", label: "Trx. Ref.", kind: "text", width: 18 },
            { key: "date", label: "Date", kind: "date", width: 12 },
            { key: "customer", label: "Customer", kind: "text", width: 26 },
            { key: "customerGroup", label: "Customer Group", kind: "text", width: 18 },
            { key: "broker", label: "Broker", kind: "text", width: 18 },
            { key: "certificate", label: "Certificate No.", kind: "text", width: 22 },
            { key: "clientRef", label: "Client's Ref. No.", kind: "text", width: 16 },
            { key: "beneficiaryAccount", label: "Beneficiary's Account No.", kind: "text", width: 20 },
            { key: "status", label: "Status", kind: "text", width: 12 },
            { key: "commodity", label: "Commodity", kind: "text", width: 22 },
            { key: "measurementUnit", label: "Measurement Unit", kind: "text", width: 14 },
            { key: "location", label: "Location", kind: "text", width: 24 },
            { key: "trxCurrency", label: "Trx. Cry", kind: "text", width: 9 },
            { key: "quantity", label: "Quantity", kind: "number", width: 12 },
            { key: "unitPrice", label: "Price/Unit", group: TRX, kind: "number", width: 12 },
            { key: "totalTrx", label: "Total Value", group: TRX, kind: "money", width: 16 },
            { key: "customerCurrency", label: "Currency", group: CUSTOMER_CCY, kind: "text", width: 9 },
            { key: "totalCustomer", label: "Total Value", group: CUSTOMER_CCY, kind: "money", width: 16 },
            { key: "brokerCurrency", label: "Currency", group: BROKER_CCY, kind: "text", width: 9 },
            { key: "totalBroker", label: "Total Value", group: BROKER_CCY, kind: "money", width: 16 },
            { key: "ctcCommBroker", label: "CTC Comm.", group: BROKER_CCY, kind: "money", width: 12 },
            { key: "totalKwd", label: "Total Value", group: BASE, kind: "money", width: 16 },
            { key: "ctcCommKwd", label: "CTC Comm.", group: BASE, kind: "money", width: 12 },
            { key: "beneficiaryPhone", label: "Beneficiary's Phone No.", kind: "text", width: 16 },
            { key: "requestedUser", label: "Requested User", kind: "text", width: 24 },
            { key: "acceptUser", label: "Accept User", kind: "text", width: 24 },
            { key: "approvedUser", label: "Approved User", kind: "text", width: 24 },
        ],
    },
};

export const CTC_REPORT_MAX_DAYS = 366;

/** ctc_config/reports: settings for the Excel reports. `logo` is a PNG/JPEG data URL (base64). */
export const CTC_REPORT_SETTINGS_DOC = "ctc_config/reports";
export const CTC_REPORT_LOGO_MAX_BYTES = 300 * 1024; // keeps the document far below Firestore's 1 MB
export interface CtcReportSettings {
    logo?: string; // base64 of a PNG/JPEG: plain ("iVBOR...") or a data URL ("data:image/png;base64,iVBOR...")
    logoUpdatedBy?: string;
    logoUpdatedAt?: { toDate: () => Date }; // Firestore Timestamp
    /** Always receive every report (all customers), in addition to the person who asked for it. */
    emails?: string[];
    emailsUpdatedBy?: string;
    emailsUpdatedAt?: { toDate: () => Date };
}

/**
 * Image type and pixel size of a PNG / JPEG logo given as base64, plain or as a data URL, or
 * undefined if it isn't one. The type comes from the image bytes, not from the data URL label.
 */
export const ctcParseLogo = (logo: unknown): { extension: "png" | "jpeg"; base64: string; width: number; height: number } | undefined => {
    if (typeof logo !== "string") return undefined;
    const base64 = logo.trim().replace(/^data:image\/[a-z+.-]+;base64,/i, "").replace(/\s+/g, "");
    if (!base64 || !/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) return undefined;
    let bin: string;
    try {
        bin = atob(base64); // browsers and Node 16+ (no Buffer: this file is also compiled by the webapp)
    } catch {
        return undefined;
    }
    const byte = (i: number) => bin.charCodeAt(i);
    const extension = bin.slice(1, 4) === "PNG" ? "png" : byte(0) === 0xff && byte(1) === 0xd8 ? "jpeg" : undefined;
    if (!extension) return undefined;
    let width = 0;
    let height = 0;
    if (extension === "png") {
        width = (byte(16) << 24 | byte(17) << 16 | byte(18) << 8 | byte(19)) >>> 0; // IHDR
        height = (byte(20) << 24 | byte(21) << 16 | byte(22) << 8 | byte(23)) >>> 0;
    } else {
        for (let i = 2; i < bin.length - 9;) { // walk JPEG segments to the first SOFn frame header
            if (byte(i) !== 0xff) return undefined;
            const marker = byte(i + 1);
            const length = byte(i + 2) << 8 | byte(i + 3);
            if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)) {
                height = byte(i + 5) << 8 | byte(i + 6);
                width = byte(i + 7) << 8 | byte(i + 8);
                break;
            }
            i += 2 + length;
        }
    }
    return width > 0 && height > 0 ? { extension, base64, width, height } : undefined;
};
/** Guard only: reports are built in the background and emailed in parts, so a year at 500+ orders a day is fine. */
export const CTC_REPORT_MAX_ROWS = 1_000_000;

/** Column label as shown on the Reports page ("Total Value (Base Currency - KWD)"). */
export const ctcReportColumnLabel = (c: CtcReportColumn) => (c.group ? `${c.label} (${c.group})` : c.label);

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** yyyy-mm-dd that is a real calendar day (2026-02-31 is not: Date.UTC would roll it to 3 March). */
const isCalendarDay = (s: unknown): s is string => {
    if (typeof s !== "string" || !ISO_DAY.test(s)) return false;
    const [y, m, d] = s.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
};

/**
 * Kuwait calendar days (yyyy-mm-dd) → [start, end) instants. Kuwait is UTC+3 all year (no DST),
 * so the day starts at 21:00 UTC the evening before.
 */
export const ctcKuwaitDayRange = (from: string, to: string): { start: Date; end: Date } => {
    const day = (s: string, add = 0) => {
        const [y, m, d] = s.split("-").map(Number);
        return new Date(Date.UTC(y, m - 1, d + add) - 3 * 3600_000);
    };
    return { start: day(from), end: day(to, 1) };
};

/** Why this report request can't run, or undefined. Same check on the page and on the server. */
export const ctcReportRequestProblem = (r: { report?: unknown; from?: unknown; to?: unknown; columns?: unknown }): string | undefined => {
    if (r.report !== "summary" && r.report !== "details") return "Choose a report";
    if (!isCalendarDay(r.from) || !isCalendarDay(r.to)) return "Choose a start and end date";
    const { start, end } = ctcKuwaitDayRange(r.from, r.to);
    if (start >= end) return "The end date must be on or after the start date";
    if ((end.getTime() - start.getTime()) / 86400_000 > CTC_REPORT_MAX_DAYS) return `Choose at most ${CTC_REPORT_MAX_DAYS} days`;
    if (!Array.isArray(r.columns) || r.columns.length === 0) return "Choose at least one column";
    const known = new Set(CTC_REPORTS[r.report].columns.map((c) => c.key));
    if (r.columns.some((k) => typeof k !== "string" || !known.has(k))) return "Unknown column";
    return undefined;
};

/** PO states a report can filter on (drafts and deleted orders are never reported). */
export const CTC_REPORT_STATES: CtcPoState[] = ["Pending", "Submitted", "Accepted", "Purchased", "Transferred", "Completed", "Canceled", "Canceled after completed"];
export const CTC_REPORT_DEFAULT_STATES: CtcPoState[] = ["Completed"];

/** Optional filters next to the period (legacy Reports search). Empty / missing = no filter. */
export interface CtcReportFilters {
    states?: CtcPoState[]; // empty = every reportable state
    reference?: string; // reference number, from the start, case-insensitive ("MBK-25-03")
    customerId?: string; // desk users only; bank users are always limited to their customer
    clientRef?: string; // client's reference, contains, case-insensitive
    beneficiaryNo?: string; // beneficiary number (the bank's reference for the end client), contains, case-insensitive
    currencyId?: string; // transaction (account) currency
}

/** Status codes to query for the chosen states (≤ 30 values, a Firestore `in` limit). */
export const ctcReportStatusCodes = (states: CtcPoState[] | undefined): CtcPoStatusCode[] => {
    const wanted = new Set(states?.length ? states : CTC_REPORT_STATES);
    return Object.values(CtcPoStatusCode).filter((code) => wanted.has(ctcPoStateOf(code)));
};

const norm = (v: unknown) => `${v ?? ""}`.trim().toUpperCase();

/** Why these filters can't be used, or undefined. */
export const ctcReportFiltersProblem = (f: CtcReportFilters | undefined): string | undefined => {
    if (!f) return undefined;
    if (f.states !== undefined && (!Array.isArray(f.states) || f.states.some((s) => !CTC_REPORT_STATES.includes(s)))) return "Unknown PO status";
    for (const [key, label] of [["reference", "Reference number"], ["clientRef", "Client ref no."], ["beneficiaryNo", "Beneficiary number"], ["customerId", "Customer"], ["currencyId", "Account currency"]] as const) {
        const v = f[key];
        if (v !== undefined && v !== null && (typeof v !== "string" || v.length > 60)) return `${label} is not valid`;
    }
    return undefined;
};

/** The filters that aren't part of the Firestore query (reference, client ref, beneficiary no., customer, currency). */
export const ctcReportMatches = (
    po: { referenceNumber?: string; clientRefNo?: string; beneficiary?: { number?: string }; customerId?: string; currencyId?: string },
    f: CtcReportFilters | undefined,
): boolean => {
    if (!f) return true;
    if (norm(f.reference) && !norm(po.referenceNumber).startsWith(norm(f.reference))) return false;
    if (norm(f.clientRef) && !norm(po.clientRefNo).includes(norm(f.clientRef))) return false;
    if (norm(f.beneficiaryNo) && !norm(po.beneficiary?.number).includes(norm(f.beneficiaryNo))) return false;
    if (f.customerId && po.customerId !== f.customerId) return false;
    if (f.currencyId && norm(po.currencyId) !== norm(f.currencyId)) return false;
    return true;
};

/* ---------- Background reports, emailed (ctc_report_requests) ---------- */

export const CTC_REPORT_REQUESTS = "ctc_report_requests";
/** Report emails only go to company addresses (they hold every customer's orders). */
export const CTC_REPORT_EMAIL_DOMAINS = ["kfh.com", "kfhcapital.com.kw", "kfhbrokerage.com"];
export const CTC_REPORT_MAX_EMAILS = 30;
export const CTC_REPORT_MAX_SENDS = 50; // first send + re-sends per request
/**
 * Cells per Excel file. Above this the report is split into parts, one email each, so every email
 * stays under SendGrid's 30 MB (attachments grow by 1/3 when encoded): about 74,000 rows of
 * Details (27 columns) or 150,000 rows of Summary (13 columns) per file.
 */
export const CTC_REPORT_CELLS_PER_PART = 2_000_000;
export const ctcReportRowsPerPart = (columnCount: number) => Math.max(1000, Math.floor(CTC_REPORT_CELLS_PER_PART / Math.max(columnCount, 1)));

export type CtcReportSendStatus = "queued" | "running" | "sent" | "empty" | "failed";
type Stamp = { toDate: () => Date };

/** One email run of a request: the first one, or a re-send (built again with the same filters). */
export interface CtcReportSend {
    id: string;
    resend: boolean;
    by: string; // email
    byName: string;
    at: Stamp;
    status: CtcReportSendStatus;
    finishedAt?: Stamp;
    rows?: number;
    parts?: number;
    bytes?: number;
    recipients?: string[];
    error?: string;
}

/** ctc_report_requests/{id}: written only by functions-ctc. Also the report history. */
export interface CtcReportRequest {
    id?: string;
    report: CtcReportKind;
    title: string;
    from: string; // yyyy-mm-dd, Kuwait
    to: string;
    columns: string[];
    filters: CtcReportFilters; // as applied (bank users: their customer)
    filterText: string; // "Status: Completed · Customer: …" as printed in the Excel
    filterLines: { label: string; value: string }[]; // the same filters, one per line (email, history details)
    /** Customer of a customer-bound requester (bank user): who else may see this request. null = desk request. */
    customerId: string | null;
    requestedBy: string;
    requestedByName: string;
    requestedAt: Stamp;
    /** Status of the latest send (mirrors sends[last]). */
    status: CtcReportSendStatus;
    activeSendId: string;
    progress?: number; // orders read so far by the running send
    rows?: number;
    parts?: number;
    error?: string;
    sends: CtcReportSend[];
    attempts?: number;
    leaseUntil?: Stamp;
    leaseOwner?: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Why this address can't receive reports, or undefined. */
export const ctcReportEmailProblem = (email: string): string | undefined => {
    const e = email.trim().toLowerCase();
    if (!EMAIL.test(e)) return `${email} is not an email address`;
    if (!CTC_REPORT_EMAIL_DOMAINS.includes(e.split("@")[1])) return `${email}: only ${CTC_REPORT_EMAIL_DOMAINS.join(", ")} addresses`;
    return undefined;
};

/** Requester first, then the configured list; lower case, no duplicates, company addresses only. */
export const ctcReportRecipients = (requester: string, configured: unknown): string[] => {
    const list = [requester, ...(Array.isArray(configured) ? configured : [])]
        .filter((e): e is string => typeof e === "string")
        .map((e) => e.trim().toLowerCase())
        .filter((e) => !ctcReportEmailProblem(e));
    return [...new Set(list)];
};
